import React, { useEffect, useState, useRef } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { CheckIcon, ArrowRightIcon, ChevronRightIcon, ChevronDownIcon, BuildingIcon, UsersIcon, BarChartIcon, FileTextIcon, ShieldIcon, HeartIcon, BriefcaseIcon, UserIcon, DatabaseIcon, ServerIcon, GlobeIcon, BookOpenIcon, AwardIcon, SmartphoneIcon, RefreshCwIcon, ClipboardCheckIcon, BadgeCheckIcon, ClockIcon, MailIcon, LockIcon, CreditCardIcon, InfinityIcon, LibraryIcon } from 'lucide-react';
export const Homepage = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white text-[#0e1114]" data-id="element-2643">
      <style jsx global data-id="element-2644">{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        body {
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .text-gradient {
          background: linear-gradient(90deg, #0b757a, #1ddbdb);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      <Header data-id="element-2645" />
      <main className="flex-grow" data-id="element-2646">
        <HeroSection data-id="element-2647" />
        <TrustStrip data-id="element-2648" />
        <WhyKivarroSection data-id="element-2649" />
        <UnifiedBenefitsSection data-id="element-2650" />
        <ForTeamsSection data-id="element-2651" />
        <AllAccessSection data-id="element-2652" />
        <EnterpriseAcademySection data-id="element-2653" />
        <ExploreTrainingSection data-id="element-2654" />
        <ClosingCta data-id="element-2655" />
      </main>
      <Footer data-id="element-2656" />
    </div>;
};
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="relative pt-32 pb-24 md:pt-40 md:pb-32 lg:pt-44 lg:pb-40 overflow-hidden" data-id="element-2657">
      {/* Background image with subtle overlay */}
      <div className="absolute inset-0 z-0" data-id="element-2658">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80')",
        opacity: 0.15
      }} data-id="element-2659"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2660">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2661">
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '100ms'
        }} data-id="element-2662">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-8 text-[#0e1114]" data-id="element-2663">
              Train. Upskill. <br className="hidden sm:block" data-id="element-2664" />
              Stay Compliant.
            </h1>
          </div>
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '300ms'
        }} data-id="element-2665">
            <p className="text-xl md:text-2xl text-[#0e1114]/80 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-2666">
              Accredited compliance, workplace skills, and business training —
              built for Ireland, the UK, and Europe.
            </p>
          </div>
          <div className={`flex flex-col sm:flex-row gap-5 justify-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '500ms'
        }} data-id="element-2667">
            <a href="/training" className="px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 text-center shadow-lg" data-id="element-2668">
              Start Training
            </a>
            <a href="/demo" className="px-8 py-4 border-2 border-[#2b2b2b] text-[#2b2b2b] font-medium rounded-md hover:bg-[#2b2b2b] hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2b2b2b] focus:ring-offset-2 text-center" data-id="element-2669">
              Book a Demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};
const TrustStrip = () => {
  return <section className="py-12 border-y border-[#f4f5f6] bg-white" data-id="element-2670">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2671">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16" data-id="element-2672">
          <div className="flex flex-col items-center" data-id="element-2673">
            <span className="text-[#0e1114]/60 text-sm font-medium mb-3" data-id="element-2674">
              CPD Accredited
            </span>
            <div className="h-10 w-24 bg-[#f4f5f6] rounded" data-id="element-2675"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-2676">
            <span className="text-[#0e1114]/60 text-sm font-medium mb-3" data-id="element-2677">
              ROSPA Member
            </span>
            <div className="h-10 w-24 bg-[#f4f5f6] rounded" data-id="element-2678"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-2679">
            <span className="text-[#0e1114]/60 text-sm font-medium mb-3" data-id="element-2680">
              Irish Trade Federation
            </span>
            <div className="h-10 w-24 bg-[#f4f5f6] rounded" data-id="element-2681"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-2682">
            <div className="flex" data-id="element-2683">
              {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-[#0b757a]" fill="currentColor" viewBox="0 0 20 20" data-id="element-2684">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-2685" />
                </svg>)}
            </div>
            <span className="mt-2 text-[#0e1114]/60 text-sm font-medium" data-id="element-2686">
              97% Completion Rate in 2024
            </span>
          </div>
        </div>
      </div>
    </section>;
};
const WhyKivarroSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-2687">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2688">
        <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-2689">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-2690">
            One academy. Every advantage.
          </h2>
          <p className="text-xl text-[#0e1114]/70 max-w-2xl mx-auto" data-id="element-2691">
            Kivarro isn't just courses — it's a training ecosystem. Whether
            you're an individual, a manager, or an enterprise, every learner
            gets the same premium experience.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-2692">
          <div className={`bg-[#f4f5f6] rounded-lg p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '100ms'
        }} data-id="element-2693">
            <div className="w-14 h-14 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-6" data-id="element-2694">
              <UserIcon className="w-7 h-7 text-[#0b757a]" data-id="element-2695" />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#0e1114]" data-id="element-2696">
              For Individuals
            </h3>
            <p className="text-[#0e1114]/70" data-id="element-2697">
              Access professional training anytime, anywhere. Get certified
              instantly and boost your career prospects.
            </p>
          </div>
          <div className={`bg-[#0b757a] text-white rounded-lg p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '300ms'
        }} data-id="element-2698">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-2699">
              <UsersIcon className="w-7 h-7 text-white" data-id="element-2700" />
            </div>
            <h3 className="text-2xl font-bold mb-3" data-id="element-2701">For Teams</h3>
            <p className="text-white/90" data-id="element-2702">
              Assign, track, and manage training for your entire team with
              built-in dashboards and reporting tools.
            </p>
          </div>
          <div className={`bg-[#2b2b2b] text-white rounded-lg p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '500ms'
        }} data-id="element-2703">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-2704">
              <BuildingIcon className="w-7 h-7 text-white" data-id="element-2705" />
            </div>
            <h3 className="text-2xl font-bold mb-3" data-id="element-2706">For Enterprises</h3>
            <p className="text-white/90" data-id="element-2707">
              Scale training across your organization with your own branded
              academy, SSO, and advanced analytics.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const UnifiedBenefitsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-[#f4f5f6]" data-id="element-2708">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2709">
        <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-2710">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-2711">
            Everything you expect. And more.
          </h2>
        </div>
        <div className={`grid grid-cols-12 gap-6 max-w-6xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-2712">
          {/* Row 1 */}
          <div className="col-span-12 md:col-span-6 bg-white rounded-lg p-8 shadow-sm flex" data-id="element-2713">
            <div className="mr-6" data-id="element-2714">
              <div className="w-14 h-14 rounded-full bg-[#0b757a]/10 flex items-center justify-center" data-id="element-2715">
                <ClockIcon className="w-7 h-7 text-[#0b757a]" data-id="element-2716" />
              </div>
            </div>
            <div data-id="element-2717">
              <h3 className="text-2xl font-bold mb-3 text-[#0e1114]" data-id="element-2718">
                Start Instantly
              </h3>
              <p className="text-[#0e1114]/70" data-id="element-2719">
                Sign up and begin training right away — no delays, no waiting
                periods.
              </p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 bg-[#2b2b2b] rounded-lg p-8 text-white flex" data-id="element-2720">
            <div className="mr-6" data-id="element-2721">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center" data-id="element-2722">
                <RefreshCwIcon className="w-7 h-7 text-white" data-id="element-2723" />
              </div>
            </div>
            <div data-id="element-2724">
              <h3 className="text-2xl font-bold mb-3" data-id="element-2725">
                Train Anytime, Anywhere
              </h3>
              <p className="text-white/90" data-id="element-2726">
                24/7 access across desktop, mobile, and tablet — learn on your
                schedule.
              </p>
            </div>
          </div>
          {/* Row 2 */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-2727">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-2728">
              <SmartphoneIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2729" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2730">
              Dedicated Mobile App
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-2731">
              Download courses and train offline — even without internet.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-2732">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-2733">
              <BadgeCheckIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2734" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2735">
              Instant Certification
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-2736">
              Certificates issued the moment you complete a course.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-[#0b757a] rounded-lg p-8 text-white" data-id="element-2737">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5" data-id="element-2738">
              <AwardIcon className="w-6 h-6 text-white" data-id="element-2739" />
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-2740">Certificate Guarantee</h3>
            <p className="text-white/90 text-sm" data-id="element-2741">
              Always accepted by auditors and regulators.
            </p>
          </div>
          {/* Row 3 */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-2742">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-2743">
              <LockIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2744" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2745">
              Stored Forever
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-2746">
              All certificates and compliance records kept in one secure hub.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-2747">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-2748">
              <InfinityIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2749" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2750">
              Unlimited Retries
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-2751">
              Train and retest until you pass — no extra fees.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-[#2b2b2b] rounded-lg p-8 text-white" data-id="element-2752">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5" data-id="element-2753">
              <LibraryIcon className="w-6 h-6 text-white" data-id="element-2754" />
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-2755">Vast Course Library</h3>
            <p className="text-white/90 text-sm" data-id="element-2756">
              Compliance, workplace skills, and business courses that grow with
              you.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const ForTeamsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-2757">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2758">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto" data-id="element-2759">
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-2760">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-[#0e1114] tracking-tight" data-id="element-2761">
              Training that scales effortlessly.
            </h2>
            <div className="space-y-6 mb-10" data-id="element-2762">
              <div className="flex" data-id="element-2763">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-2764">
                  <MailIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2765" />
                </div>
                <div data-id="element-2766">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2767">
                    Roll out training instantly
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-2768">
                    Automated login emails are sent as soon as staff are added.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-2769">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-2770">
                  <CreditCardIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2771" />
                </div>
                <div data-id="element-2772">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2773">
                    Add seats anytime
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-2774">
                    Scale your training without needing to contact support.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-2775">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-2776">
                  <BarChartIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2777" />
                </div>
                <div data-id="element-2778">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2779">
                    Track progress and export reports
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-2780">
                    Manage compliance with a built-in dashboard.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-2781">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-2782">
                  <RefreshCwIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2783" />
                </div>
                <div data-id="element-2784">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2785">
                    Reassign licenses
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-2786">
                    Easily transfer seats as teams change.
                  </p>
                </div>
              </div>
            </div>
            <a href="/teams" className="inline-flex items-center px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 shadow-lg" data-id="element-2787">
              Train Your Team
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2788" />
            </a>
          </div>
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '200ms'
        }} data-id="element-2789">
            <div className="relative rounded-lg overflow-hidden shadow-2xl" data-id="element-2790">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Team Training Dashboard" className="w-full h-auto object-cover" data-id="element-2791" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e1114]/50 to-transparent" data-id="element-2792"></div>
              <div className="absolute bottom-8 left-8 right-8" data-id="element-2793">
                <div className="bg-white p-6 rounded-lg shadow-lg" data-id="element-2794">
                  <div className="flex items-center justify-between mb-4" data-id="element-2795">
                    <h4 className="text-lg font-bold text-[#0e1114]" data-id="element-2796">
                      Team Dashboard
                    </h4>
                    <div className="flex" data-id="element-2797">
                      <div className="w-2 h-2 rounded-full bg-[#0b757a] mr-1 animate-pulse" data-id="element-2798"></div>
                      <div className="w-2 h-2 rounded-full bg-[#0b757a]/60 mr-1 animate-pulse" style={{
                      animationDelay: '0.2s'
                    }} data-id="element-2799"></div>
                      <div className="w-2 h-2 rounded-full bg-[#0b757a]/30 animate-pulse" style={{
                      animationDelay: '0.4s'
                    }} data-id="element-2800"></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2801">
                    <div data-id="element-2802">
                      <div className="text-[#0e1114]/60" data-id="element-2803">Assigned</div>
                      <div className="text-xl font-bold text-[#0e1114]" data-id="element-2804">24</div>
                    </div>
                    <div data-id="element-2805">
                      <div className="text-[#0e1114]/60" data-id="element-2806">In Progress</div>
                      <div className="text-xl font-bold text-[#0e1114]" data-id="element-2807">18</div>
                    </div>
                    <div data-id="element-2808">
                      <div className="text-[#0e1114]/60" data-id="element-2809">Completed</div>
                      <div className="text-xl font-bold text-[#0e1114]" data-id="element-2810">6</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const AllAccessSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-[#f4f5f6]" data-id="element-2811">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2812">
        <div className="max-w-6xl mx-auto" data-id="element-2813">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2814">
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-2815">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-2816">
                Unlimited learning. <br data-id="element-2817" />
                One subscription.
              </h2>
              <p className="text-xl text-[#0e1114]/70 mb-8" data-id="element-2818">
                For learners and businesses who never stop growing, Kivarro
                offers All-Access Membership — one subscription unlocks the
                entire training library.
              </p>
              <div className="space-y-4 mb-10" data-id="element-2819">
                <div className="flex items-start" data-id="element-2820">
                  <CheckIcon className="w-6 h-6 text-[#0b757a] mt-0.5 mr-3 flex-shrink-0" data-id="element-2821" />
                  <p className="text-[#0e1114]/80 text-lg" data-id="element-2822">
                    Every course. Every update. Always included.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-2823">
                  <CheckIcon className="w-6 h-6 text-[#0b757a] mt-0.5 mr-3 flex-shrink-0" data-id="element-2824" />
                  <p className="text-[#0e1114]/80 text-lg" data-id="element-2825">
                    Ideal for continuous upskilling and compliance.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-2826">
                  <CheckIcon className="w-6 h-6 text-[#0b757a] mt-0.5 mr-3 flex-shrink-0" data-id="element-2827" />
                  <p className="text-[#0e1114]/80 text-lg" data-id="element-2828">
                    Simple pricing for individuals, teams, and enterprises.
                  </p>
                </div>
              </div>
              <a href="/membership" className="inline-flex items-center px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 shadow-lg" data-id="element-2829">
                Explore Membership
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2830" />
              </a>
            </div>
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
            transitionDelay: '200ms'
          }} data-id="element-2831">
              <div className="bg-white rounded-xl overflow-hidden shadow-xl" data-id="element-2832">
                <div className="bg-[#2b2b2b] p-8 text-white" data-id="element-2833">
                  <div className="inline-flex items-center px-3 py-1 bg-[#1ddbdb]/90 text-white text-sm font-medium rounded-full mb-4" data-id="element-2834">
                    Most Popular
                  </div>
                  <h3 className="text-3xl font-bold mb-2" data-id="element-2835">
                    All-Access Membership
                  </h3>
                  <p className="text-white/80" data-id="element-2836">
                    Unlimited training for continuous learning
                  </p>
                </div>
                <div className="p-8" data-id="element-2837">
                  <div className="flex items-baseline mb-6" data-id="element-2838">
                    <span className="text-4xl font-bold text-[#0e1114]" data-id="element-2839">
                      €39
                    </span>
                    <span className="text-[#0e1114]/60 ml-2" data-id="element-2840">
                      / month per user
                    </span>
                  </div>
                  <div className="space-y-4 mb-8" data-id="element-2841">
                    <div className="flex items-center" data-id="element-2842">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-2843" />
                      <p data-id="element-2844">Full access to all 125+ courses</p>
                    </div>
                    <div className="flex items-center" data-id="element-2845">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-2846" />
                      <p data-id="element-2847">Unlimited certifications</p>
                    </div>
                    <div className="flex items-center" data-id="element-2848">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-2849" />
                      <p data-id="element-2850">New courses as they're released</p>
                    </div>
                    <div className="flex items-center" data-id="element-2851">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-2852" />
                      <p data-id="element-2853">Mobile app access</p>
                    </div>
                    <div className="flex items-center" data-id="element-2854">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-2855" />
                      <p data-id="element-2856">Team management dashboard</p>
                    </div>
                  </div>
                  <a href="/membership/signup" className="block w-full py-3 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] text-center" data-id="element-2857">
                    Start Free Trial
                  </a>
                  <p className="text-center text-[#0e1114]/60 text-sm mt-3" data-id="element-2858">
                    30-day free trial, cancel anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const EnterpriseAcademySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-[#2b2b2b] text-white" data-id="element-2859">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2860">
        <div className="max-w-6xl mx-auto" data-id="element-2861">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2862">
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-2863">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-2864">
                Your academy. <br data-id="element-2865" />
                Your brand.
              </h2>
              <p className="text-white/80 text-xl mb-8" data-id="element-2866">
                Go beyond training — launch your own fully branded compliance
                academy.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-2867">
                <div className="flex items-start" data-id="element-2868">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2869">
                    <GlobeIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-2870" />
                  </div>
                  <div data-id="element-2871">
                    <h3 className="font-bold text-white mb-1" data-id="element-2872">
                      Custom branding
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-2873">
                      Your logo, colors, and domain
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2874">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2875">
                    <BarChartIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-2876" />
                  </div>
                  <div data-id="element-2877">
                    <h3 className="font-bold text-white mb-1" data-id="element-2878">
                      Advanced analytics
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-2879">
                      Detailed reporting and insights
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2880">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2881">
                    <ServerIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-2882" />
                  </div>
                  <div data-id="element-2883">
                    <h3 className="font-bold text-white mb-1" data-id="element-2884">
                      SSO & provisioning
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-2885">
                      Seamless user management
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2886">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2887">
                    <HeartIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-2888" />
                  </div>
                  <div data-id="element-2889">
                    <h3 className="font-bold text-white mb-1" data-id="element-2890">
                      Priority support
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-2891">
                      Dedicated account manager
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2892">
                <a href="/enterprise/demo" className="px-8 py-4 bg-white text-[#2b2b2b] font-medium rounded-md transition-all duration-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#2b2b2b] text-center shadow-lg" data-id="element-2893">
                  Book a Demo
                </a>
                <a href="/enterprise" className="px-8 py-4 border-2 border-white text-white font-medium rounded-md hover:bg-white/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#2b2b2b] text-center" data-id="element-2894">
                  Learn More
                </a>
              </div>
            </div>
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
            transitionDelay: '200ms'
          }} data-id="element-2895">
              <div className="relative rounded-lg overflow-hidden shadow-2xl" data-id="element-2896">
                <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Enterprise Academy Dashboard" className="w-full h-auto object-cover" data-id="element-2897" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2b2b2b]/60 to-transparent" data-id="element-2898"></div>
                <div className="absolute top-6 left-6 right-6 flex justify-between items-center" data-id="element-2899">
                  <div className="flex items-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg" data-id="element-2900">
                    <div className="w-6 h-6 bg-[#0b757a] rounded-md mr-3" data-id="element-2901"></div>
                    <span className="font-medium text-[#2b2b2b]" data-id="element-2902">
                      Your Brand
                    </span>
                  </div>
                  <div className="flex space-x-2" data-id="element-2903">
                    <div className="w-3 h-3 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-2904"></div>
                    <div className="w-3 h-3 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-2905"></div>
                    <div className="w-3 h-3 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-2906"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const ExploreTrainingSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const categories = [{
    icon: <ShieldIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2907" />,
    title: 'Compliance',
    description: 'Essential regulatory training for all industries.',
    link: '/training/compliance'
  }, {
    icon: <HeartIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2908" />,
    title: 'Health & Safety',
    description: 'Workplace safety and risk management training.',
    link: '/training/health-safety'
  }, {
    icon: <BookOpenIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2909" />,
    title: 'Food Safety',
    description: 'HACCP and food handling certifications.',
    link: '/training/food-safety'
  }, {
    icon: <UserIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2910" />,
    title: 'HR & People',
    description: 'Employee management and workplace culture.',
    link: '/training/hr-people'
  }, {
    icon: <UsersIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2911" />,
    title: 'Leadership & Management',
    description: 'Team leadership and management skills.',
    link: '/training/leadership'
  }, {
    icon: <HeartIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2912" />,
    title: 'Customer Service',
    description: 'Client relationship and service excellence.',
    link: '/training/customer-service'
  }, {
    icon: <BriefcaseIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2913" />,
    title: 'Digital & Marketing',
    description: 'Digital skills and marketing strategies.',
    link: '/training/digital-marketing'
  }, {
    icon: <DatabaseIcon className="w-6 h-6 text-[#0b757a]" data-id="element-2914" />,
    title: 'Data, GDPR & Security',
    description: 'Data protection and cybersecurity training.',
    link: '/training/data-security'
  }];
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-2915">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2916">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2917">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-2918">
            Every skill. Every sector.
          </h2>
          <p className="text-xl text-[#0e1114]/70" data-id="element-2919">
            From mandatory compliance to leadership, digital skills, and
            business growth.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-2920">
          {categories.map((category, index) => <div key={index} className={`bg-[#f4f5f6] rounded-lg p-6 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: `${index * 50 + 100}ms`
        }} data-id="element-2921">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4" data-id="element-2922">
                {category.icon}
              </div>
              <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-2923">
                {category.title}
              </h3>
              <p className="text-[#0e1114]/70 text-sm mb-4" data-id="element-2924">
                {category.description}
              </p>
              <a href={category.link} className="text-[#0b757a] font-medium hover:text-[#1ddbdb] transition-colors flex items-center text-sm" data-id="element-2925">
                Explore Courses
                <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-2926" />
              </a>
            </div>)}
        </div>
        <div className="text-center mt-12" data-id="element-2927">
          <a href="/training" className="px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 inline-block shadow-lg" data-id="element-2928">
            View All Training Courses
          </a>
        </div>
      </div>
    </section>;
};
const ClosingCta = () => {
  return <section className="py-24 bg-[#f4f5f6]" data-id="element-2929">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2930">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2931">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-2932">
            The Training Academy for Ireland, the UK & Europe.
          </h2>
          <p className="text-xl text-[#0e1114]/70 mb-10 max-w-3xl mx-auto" data-id="element-2933">
            Individuals. Teams. Enterprises. One platform. Instant access.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center" data-id="element-2934">
            <a href="/training" className="px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 text-center shadow-lg" data-id="element-2935">
              Start Training
            </a>
            <a href="/demo" className="px-8 py-4 border-2 border-[#2b2b2b] text-[#2b2b2b] font-medium rounded-md hover:bg-[#2b2b2b] hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2b2b2b] focus:ring-offset-2 text-center" data-id="element-2936">
              Book a Demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};